import DonateFormAddressPopoutForm from '~/javascripts/components/DonateForm/AddressPopoutForm';
import DonateFormButton from '~/javascripts/components/DonateForm/Button';
import DonateFormDonorInfoPopoutForm from '~/javascripts/components/DonateForm/DonorInfoPopoutForm';
import DonateFormSavedPaymentMethods from '~/javascripts/components/DonateForm/SavedPaymentMethods';
import cx from 'classnames';
import store from '~/javascripts/store';
import { CardCvcElement, CardElement } from '@stripe/react-stripe-js';
import { Component } from 'react';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import { pauseForSpecs } from '~/javascripts/utils/testing';

const StripeElements = observer(
  class StripeElements extends Component {
    static cardValidationErrorMessages = {
      empty: "YOUR CARD'S INFORMATION IS INCOMPLETE",
      emptyCvc: "YOUR CARD'S SECURITY CODE IS INCOMPLETE",
      updatePaymentError: "HMM, THAT DIDN'T WORK",
    };

    componentDidMount() {
      store.donateForm.cardElementCompleted = false;
      if (
        store.donateForm.queryParams.updatePaymentError &&
        store.donateForm.managementMode
      ) {
        store.donateForm.cardElementStep1Error =
          StripeElements.cardValidationErrorMessages.updatePaymentError;
      } else {
        store.donateForm.cardElementStep1Error = '';
      }
      store.donateForm.creditCardStep = 1;
      store.donateForm.stripeSubmitViaEnterKeyHandler = this.handleClick;
    }

    handleCardElementChange = (event) => {
      store.donateForm.cardElementStep1Error = '';

      if (event.error) {
        store.donateForm.handleCardError(event.error);
      }

      store.donateForm.cardElementCompleted = event.complete;
    };

    handleClick = () => {
      if (!store.donateForm.cardElementCompleted) {
        store.donateForm.cardElementStep1Error =
          StripeElements.cardValidationErrorMessages.empty;
      }

      if (
        store.donateForm.cvcVerificationRequired &&
        !store.donateForm.cardCvcElementCompleted
      ) {
        store.donateForm.cardCvcElementError =
          StripeElements.cardValidationErrorMessages.emptyCvc;
      }

      if (
        !this.props.validateForm() ||
        (!store.donateForm.selectedStripePaymentMethodCard &&
          !store.donateForm.cardElementCompleted) ||
        (store.donateForm.cvcVerificationRequired &&
          !store.donateForm.cardCvcElementCompleted)
      ) {
        store.donateForm.disableFormSubmit = false;
        return false;
      }

      store.donateForm.creditCardStep === 1 && store.donateForm.showAddressStep
        ? this.handleNextStep()
        : this.handlePayment();
    };

    handleNextStep = () => {
      store.donateForm.disableFormSubmit = false;
      store.donateForm.creditCardStep = 2;

      if (!store.donateForm.managementMode) {
        store.donateForm.tracking.trackContactInfoProvidedEvent();
      }
    };

    handlePrevStep = () => {
      store.donateForm.creditCardStep = 1;
    };

    trackPayment = () => {
      this.props.trackPayButtonClick({
        button_type: 'Credit Card',
      });
    };

    handlePayment = async () => {
      store.donateForm.stripeCardElement =
        this.props.elements.getElement(CardElement);

      if (store.donateForm.cvcVerificationRequired) {
        const cardCvcElement = this.props.elements.getElement(CardCvcElement);
        const createTokenResponse = await this.props.stripe.createToken(
          'cvc_update',
          cardCvcElement
        );

        if (createTokenResponse.error) {
          store.donateForm.handleCardError(createTokenResponse.error);
          return;
        } else {
          store.donateForm.stripeCardCvcToken = createTokenResponse.token.id;
        }
      }

      if (store.donateForm.selectedStripePaymentMethodCard) {
        store.donateForm.paymentMethodId =
          store.donateForm.selectedStripePaymentMethodCard.paymentMethodId;
      } else {
        const paymentMethodResponse =
          await this.props.stripe.createPaymentMethod(
            store.donateForm.paymentMethodParams
          );

        if (paymentMethodResponse.error) {
          store.donateForm.handleCardError(paymentMethodResponse.error);
          return;
        } else {
          store.donateForm.paymentMethodId =
            paymentMethodResponse.paymentMethod.id;
        }
      }

      this.trackPayment();

      if (store.donateForm.managementMode) {
        store.donateForm.stripeCardCvcElement =
          this.props.elements.getElement(CardCvcElement);

        store.donateForm.createSetupIntent(() => {
          this.props.stripe
            .confirmCardSetup(
              store.donateForm.setupIntentSecret,
              store.donateForm.confirmSetupIntentParams
            )
            .then((response) => {
              if (response.error) {
                store.donateForm.handleCardError(response.error);
              } else {
                store.donateForm.createDonationForm(
                  'stripe',
                  ({ redirectUrl }) => {
                    window.location = redirectUrl;
                  },
                  ({ response }) => {
                    store.donateForm.handleCardError(response.error);
                  }
                );
              }
            });
        });
      } else {
        store.donateForm.processStripeDonation(
          this.handlePaymentSuccess,
          this.handlePaymentError,
          this.handlePaymentRequiresAction
        );
      }
    };

    handlePaymentRequiresAction = async ({ paymentIntentClientSecret }) => {
      let response;

      if (store.donateForm.isSubscription) {
        response = await this.props.stripe.confirmCardPayment(
          paymentIntentClientSecret
        );
      } else {
        response = await this.props.stripe.handleCardAction(
          paymentIntentClientSecret
        );
      }

      if (response.error) {
        store.donateForm.handleCardError(response.error);
      } else {
        store.donateForm.paymentIntentId = response.paymentIntent.id;

        store.donateForm.nonDebouncedProcessStripeDonation(
          this.handlePaymentSuccess,
          this.handlePaymentError,
          this.handlePaymentRequiresAction
        );
      }
    };

    handlePaymentSuccess = ({ redirectUrl }) => {
      if (store.donateForm.embedded) {
        store.donateForm.step = 'embed-success';
      } else {
        window.location = redirectUrl;
      }
    };

    handlePaymentError = (responseJSON) => {
      if (responseJSON.isSpringMember) {
        store.donateForm.isAlreadySpringMember = true;
        store.donateForm.existingMembershipCountry =
          responseJSON.existingMembershipCountry;
      } else {
        store.donateForm.handleCardError(responseJSON.error);
      }
    };

    isStripe = () => store.donateForm.paymentTab === 'stripe';
    isStripeAndStep = (step) =>
      this.isStripe() && step == store.donateForm.creditCardStep;

    hideCardElement = () =>
      !this.isStripeAndStep(1) ||
      store.donateForm.selectedStripePaymentMethodCard ||
      store.donateForm.showSpringMembershipAlreadyExistsMessage;

    handleLinkToBankOnErrorClick = (event) => {
      event.preventDefault();
      if (store.donateForm.country == 'uk') {
        store.donateForm.paymentTab = 'bacs';
        if (store.donateForm.email) {
          store.donateForm.disableFormSubmit = true;
          store.donateForm.createDonationForm('bacs', ({ redirectUrl }) => {
            pauseForSpecs(() => {
              window.location = redirectUrl;
            });
          });
        }
      } else {
        store.donateForm.paymentTab = 'plaid';
        if (store.donateForm.email) {
          store.donateForm.triggerOpenPlaidModal = true;
        }
      }
    };

    cardInputErrorMessage = () => {
      if (
        store.donateForm.isSubscription &&
        store.donateForm.cardElementStep1Error &&
        !store.donateForm.hasCvcErrorCode
      ) {
        return (
          <span>
            {store.donateForm.cardElementStep1Error}.{' '}
            <a
              className='cw-red underline'
              href='#'
              onClick={this.handleLinkToBankOnErrorClick}
            >
              Connect your bank account instead?
            </a>
          </span>
        );
      } else {
        return store.donateForm.cardElementStep1Error;
      }
    };

    showAnnualUpsellOnPaymentStep = () =>
      store.donateForm.isProfileDonation &&
      !store.donateForm.isSubscription &&
      !store.donateForm.isMycwCampaign &&
      store.donateForm.amount > store.donateForm.subscriptionMinAmount;

    render() {
      const cardElementStyles = {
        base: {
          color: '#333232',
          fontFamily:
            'Proxima Nova, Helvetica Neue, Helvetica, Arial, sans-serif',
          fontSize: '16px',
          fontSmoothing: 'antialiased',
          iconColor: '#71716c',
          '::placeholder': {
            color: '#c1c1ba',
          },
        },
        invalid: {
          color: '#fa575d',
          iconColor: '#fa575d',
        },
      };

      const cardElementClassOverrides = {
        invalid: 'error',
      };

      const buttonText =
        !store.donateForm.showAddressStep ||
        store.donateForm.creditCardStep !== 1
          ? store.donateForm.donateButtonText
          : 'Next Step';

      const calmMode = store.donateForm.isCalmMode;

      return (
        <div className='_form-wrapper mt-2'>
          {this.isStripeAndStep(1) &&
            !store.donateForm.showSpringMembershipAlreadyExistsMessage && (
              <>
                {!calmMode && (
                  <DonateFormDonorInfoPopoutForm
                    namePlaceholder='First Name'
                    surnamePlaceholder='Last Name'
                  />
                )}
              </>
            )}

          {!isEmpty(store.donateForm.stripePaymentMethodCards) && (
            <div className={cx({ hidden: this.isStripeAndStep(2) })}>
              <DonateFormSavedPaymentMethods
                cardCvcElementClassOverrides={cardElementClassOverrides}
                cardCvcElementStyles={cardElementStyles}
              />
            </div>
          )}

          {!store.donateForm.selectedStripePaymentMethodCard && (
            <CardElement
              className={cx(
                'rounded card-element-input-padding border border-solid border-grey-40',
                {
                  error: !!store.donateForm.cardElementStep1Error,
                  hidden: this.hideCardElement(),
                }
              )}
              onChange={this.handleCardElementChange}
              options={{
                classes: cardElementClassOverrides,
                hidePostalCode:
                  store.donateForm.showAddressCollection ||
                  store.donateForm.showAddressStep,
                style: cardElementStyles,
              }}
            />
          )}

          {!store.donateForm.showSpringMembershipAlreadyExistsMessage && (
            <>
              {this.isStripeAndStep(1) &&
                !store.donateForm.selectedStripePaymentMethodCard && (
                  <>
                    <ul className='parsley-errors-list'>
                      <li>{this.cardInputErrorMessage()}</li>
                    </ul>

                    {!store.donateForm.isSubscription &&
                      !store.donateForm.embedded && (
                        <div className='custom-check'>
                          <input
                            defaultChecked={store.donateForm.savePaymentMethod}
                            id='_save_payment_method'
                            name='_save_payment_method'
                            onChange={(event) => {
                              store.donateForm.savePaymentMethod =
                                event.target.checked;
                            }}
                            type='checkbox'
                            value={store.donateForm.savePaymentMethod}
                          />
                          <label htmlFor='_save_payment_method'>
                            Save my card for future&nbsp;use
                          </label>
                        </div>
                      )}
                  </>
                )}

              {this.isStripeAndStep(2) && (
                <>
                  <div
                    className='edit-details-button'
                    onClick={this.handlePrevStep}
                  >
                    <span className='h80'>Edit card details</span>
                  </div>

                  <DonateFormAddressPopoutForm />
                </>
              )}

              {this.showAnnualUpsellOnPaymentStep() &&
                this.isStripeAndStep(2) && (
                  <div className='custom-check amount-hidden'>
                    <input
                      defaultChecked={''}
                      onChange={(e) =>
                        store.donateForm.subscriptionUpsell.toggleIsOptedIn(
                          'annual',
                          e.target.checked
                        )
                      }
                      type='checkbox'
                    />
                    <label htmlFor='amount-hidden'>
                      Renew my gift every year
                      <span className='font-normal block'>
                        {`Your donation will renew on ${new Date().toLocaleString(
                          'default',
                          { month: 'long', day: 'numeric' }
                        )} annually. 100% of your
                    donation funds water projects.`}
                      </span>
                    </label>
                  </div>
                )}
              {this.isStripe() && (
                <>
                  <DonateFormButton
                    classes={`${
                      store.donateForm.isCalmMode
                        ? 'calm-donate-form-button'
                        : ''
                    }`}
                    onClick={this.handleClick}
                    text={buttonText}
                  />
                </>
              )}
            </>
          )}
        </div>
      );
    }
  }
);

export default StripeElements;
